"use client";

import { isAndroid, isIOS } from "react-device-detect";
import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { Button, ButtonProps } from "@wojo/ui";
import React, { useEffect, useState } from "react";
import { PushToAppModal } from "./PushToAppModal";

export type PushToAppButtonProps = {
    deepLinkAndDownloadUrl?: string;
    tourCode?: string;
    pushToAppText: string;
} & Partial<ButtonProps<"button" | "a">>;

export const PushToAppButton: React.FC<PushToAppButtonProps> = (props) => {
    const {
        deepLinkAndDownloadUrl,
        tourCode,
        pushToAppText,
        children,
        ...rest
    } = props;
    const [isOpen, setIsOpen] = useState(false);

    const [mode, setMode] = useState<"desktop-mode" | "mobile-mode">(
        "mobile-mode",
    );
    const isMobile = isIOS || isAndroid;
    useEffect(() => {
        if (!isMobile) {
            setMode("desktop-mode");
        }
    }, [isMobile]);

    const l10n = useI18n("account").account.pushToApp;
    const deepLink = props.deepLinkAndDownloadUrl ?? l10n.toursDeepLink.l();
    const [desktopStyles, mobileStyles] =
        mode === "desktop-mode"
            ? [undefined, { display: "none" } as const]
            : [{ display: "none" } as const, undefined];
    const desktopButtonProps: Partial<ButtonProps<"button">> = {
        ...rest,
        tag: "button",
        type: "button",
        style: desktopStyles,
        onClick: () => {
            setIsOpen(true);
            gtmSendEvent({
                event: "track",
                ga4Tracking: {
                    ga4_object: "APP_MESSAGING",
                    ga4_action: "MODAL_OPEN",
                    clickText: pushToAppText,
                    tourCode: tourCode ?? "",
                },
            });
        },
    };

    const mobileButtonProps: Partial<ButtonProps<"button" | "a">> = {
        ...rest,
        style: mobileStyles,
        tag: "a",
        type: undefined,
        href: deepLink,
    };

    return (
        <div>
            <Button {...desktopButtonProps}>{pushToAppText}</Button>
            <Button {...mobileButtonProps}>{pushToAppText}</Button>
            <PushToAppModal
                qrCodeLink={deepLink}
                qrCodeAltText={l10n.qrCodeAltText.l()}
                title={l10n.title.l()}
                subTitle={l10n.subTitle.l()}
                primaryButtonText={l10n.primaryButtonText.l()}
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "APP_MESSAGING",
                            ga4_action: "MODAL_CLOSE",
                            clickText: pushToAppText,
                            tourCode: tourCode ?? "",
                        },
                    });
                }}
                tourCode={tourCode}
                learnMoreUrl={l10n.learnMoreUrl.l()}
            />
        </div>
    );
};
